<template>
  <div
    v-if="isModalOpen"
    :style="{ display: 'block' }"
    id="table-selection-modal"
    class="modal small-modal"
    @click.self="closeModal"
  >
    <div class="modal-content-container">
      <div class="modal-content small">
        <div id="close-modal" class="close" @click="closeModal">
          <IconCloseLarge :width="40" :height="40" fill="#BFBFBF" />
        </div>

        <!-- Order Type Section -->
        <h3 class="lang-option-title">{{ t("orderType") }}</h3>
        <div class="order-type-container">
          <div class="size-options radio-input">
            <label
              class="size-checkbox"
              v-for="type in orderTypes"
              :key="type.value"
            >
              <input
                type="radio"
                name="orderType"
                :value="type.value"
                v-model="selectedOrderType"
                @change="onOrderTypeConfirmation"
              />
              <span>{{ t(type.label) }}</span>
            </label>
          </div>
        </div>

        <!-- Table Selection Section -->
        <h3 class="lang-option-title">{{ t("selectTable") }}</h3>
        <div class="empty-table" v-if="tables.length === 0">
          <p>{{ t("noTablesFound") }}</p>
        </div>
        <ScrollerContainer>
          <div class="radio-container">
            <template v-for="table in tables">
              <div
                v-if="table.is_available"
                :key="table.id"
                class="radio-option"
              >
                <input
                  type="radio"
                  :id="`table-${table.id}`"
                  name="table"
                  :value="table"
                  v-model="selectedTable"
                  @change="onTableConfirmation"
                />
                <label :for="`table-${table.id}`">
                  <span class="radio-indicator"></span>
                  <span
                    >Table:
                    {{
                      $customTextFormatter.formattedNumber(table.number)
                    }}</span
                  >
                </label>
              </div>
            </template>
          </div>

          <!-- <CustomScroller
              ref="customScroller"
              :content-ref="contentRef"
              :container-ref="containerRef"
            /> -->
        </ScrollerContainer>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useOrderStore } from "@/stores/order";
import { useBroadcastComposable } from "@/composables/useBroadcastComposable";
// import { usePanelScroller } from "@/composables/usePanelScroller";
import { useTable } from "@/composables/useTable";
import { useOrder } from "@/composables/useOrder";
import IconCloseLarge from "@/components/icons/iconCloseLarge.vue";
// import CustomScroller from "@/components/order/customScroller.vue";
import ScrollerContainer from "@/components/scroller/scrollerContainer.vue";
import Swal from "sweetalert2";
// import { DEFAULT_TABLE_IMAGE } from "@/constants/image";

const props = defineProps({
  isOpen: { type: Boolean, required: true },
});

const emit = defineEmits(["update:isOpen"]);

const { t } = useI18n();

const { broadcastOrderToCds } = useBroadcastComposable();
const { getAvailableOrderTypes } = useOrder();
const { getCurrentTable, getOrderType, updateCurrentTable, updateOrderType } =
  useOrderStore();
const { fetchTables } = useTable();
// const { containerRef, contentRef, customScroller } = usePanelScroller();

const isModalOpen = computed(() => props.isOpen);
const currentTable = computed(() => getCurrentTable());
const currentOrderType = computed(() => getOrderType());

const tables = ref([]);
const orderTypes = ref(getAvailableOrderTypes());
const selectedTable = ref(currentTable.value);
const selectedOrderType = ref(currentOrderType.value);

const formatSnakeToTitle = (text) => {
  return text
    .split("_") // Split by underscores
    .map(
      (
        word // Capitalize each word
      ) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(" "); // Join with space
};

const closeModal = () => {
  emit("update:isOpen", false);
};

const onTableConfirmation = () => {
  updateCurrentTable(selectedTable.value);
  broadcastOrderToCds(10);
  if (selectedTable.value && selectedOrderType.value) {
    closeModal();

    Swal.fire({
      position: "bottom-end",
      icon: "success",
      title: `Selected Type: ${formatSnakeToTitle(selectedOrderType.value)}`,
      text: `Selected Table: ${selectedTable.value.number}`,
      showConfirmButton: false,
      customClass: {
        container: "custom-table-alert",
      },
      timer: 1500,
    });
  }
};

const onOrderTypeConfirmation = () => {
  updateOrderType(selectedOrderType.value);
  broadcastOrderToCds(10);
  if (selectedTable.value && selectedOrderType.value) {
    closeModal();

    Swal.fire({
      position: "bottom-end",
      icon: "success",
      title: `Selected Type: ${formatSnakeToTitle(selectedOrderType.value)}`,
      text: `Selected Table: ${selectedTable.value.number}`,
      showConfirmButton: false,
      customClass: {
        container: "custom-table-alert",
      },
      timer: 1500,
    });
  }
};

onMounted(async () => {
  try {
    const fetchedTables = await fetchTables();
    tables.value = fetchedTables.sort((a, b) => a.number - b.number);
  } catch (error) {
    console.error("Error fetching tables:", error);
  }
});

watch(currentTable, (newTable) => {
  selectedTable.value = newTable;
});

watch(currentOrderType, (newOrderType) => {
  selectedOrderType.value = newOrderType;
});
</script>

<style scoped>
.size-checkbox span,
.size-checkbox input[type="radio"]:checked + span {
  padding: 0px 25px;
}

.order-type-container {
  margin-bottom: 30px;
}
</style>
